import { useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { ui } from "../theme";

// Piezas de interfaz compartidas por las secciones de Gastos y Presupuestos.

export function Page({ title, subtitle, actions, children }: { title: string; subtitle?: string; actions?: ReactNode; children: ReactNode }) {
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  return (
    <div className="w-full max-w-6xl overflow-y-auto p-4 sm:p-6 space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className={`text-xl font-bold ${c.text}`}>{title}</h1>
          {subtitle && <p className={`text-sm ${c.muted}`}>{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

export function ErrorBox({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="rounded-lg bg-red-500/10 p-3 text-sm text-red-500">
      {message}
    </p>
  );
}

export function Notice({ tone = "info", children }: { tone?: "info" | "warn" | "ok"; children: ReactNode }) {
  const tones = {
    info: "bg-sky-500/10 text-sky-700 dark:text-sky-300",
    warn: "bg-amber-500/10 text-amber-700 dark:text-amber-300",
    ok: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  };
  return <div className={`rounded-lg p-3 text-sm ${tones[tone]}`}>{children}</div>;
}

export function Kpi({ label, value, hint, tone }: { label: string; value: string; hint?: string; tone?: "bad" | "good" }) {
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const color = tone === "bad" ? "text-red-500" : tone === "good" ? "text-emerald-500" : c.text;
  return (
    <div className={c.card}>
      <p className={`text-xs ${c.muted}`}>{label}</p>
      <p className={`mt-1 text-2xl font-bold tabular-nums ${color}`}>{value}</p>
      {hint && <p className={`mt-0.5 text-xs ${c.muted}`}>{hint}</p>}
    </div>
  );
}

/** Pestañas de una sección (una vista a la vez). */
export function Tabs<T extends string>({ tabs, value, onChange, label }: { tabs: { id: T; label: string; icon?: string }[]; value: T; onChange: (id: T) => void; label: string }) {
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  return (
    <div role="tablist" aria-label={label} className={`flex flex-wrap gap-1 border-b ${c.border}`}>
      {tabs.map((tab) => {
        const active = tab.id === value;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            data-testid={`tab-${tab.id}`}
            onClick={() => onChange(tab.id)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors ${active ? "border-teal-600 text-teal-600" : `border-transparent ${c.muted} hover:text-teal-600`}`}
          >
            {tab.icon && <i className={`${tab.icon} mr-1`} />}
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

export function Chip({ className, children }: { className: string; children: ReactNode }) {
  return <span className={`inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${className}`}>{children}</span>;
}

export function Field({ label, children, className = "" }: { label: string; children: ReactNode; className?: string }) {
  const { isDarkMode } = useTheme();
  return (
    <label className={`block text-xs font-medium ${ui(isDarkMode).muted} ${className}`}>
      <span className="mb-1 block">{label}</span>
      {children}
    </label>
  );
}

export function Dialog({ open, title, onClose, children, footer, wide }: { open: boolean; title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-label={title}>
      <div className={`flex max-h-[92vh] w-full flex-col rounded-2xl border shadow-xl ${wide ? "max-w-4xl" : "max-w-2xl"} ${isDarkMode ? "bg-dark-card border-dark-border" : "bg-white border-gray-200"}`}>
        <div className={`flex items-center justify-between border-b px-5 py-3 ${c.border}`}>
          <h2 className={`text-base font-semibold ${c.text}`}>{title}</h2>
          <button type="button" onClick={onClose} aria-label="close" className={c.muted}>
            <i className="ri-close-line text-xl" />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className={`flex justify-end gap-2 border-t px-5 py-3 ${c.border}`}>{footer}</div>}
      </div>
    </div>
  );
}

/** Zona para soltar o elegir archivos. */
export function FileDrop({
  accept,
  multiple,
  onFiles,
  disabled,
  label,
  hint,
}: {
  accept: string;
  multiple?: boolean;
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  label: string;
  hint?: string;
}) {
  const { isDarkMode } = useTheme();
  const c = ui(isDarkMode);
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    <div
      role="button"
      tabIndex={0}
      aria-disabled={disabled}
      onClick={() => !disabled && input.current?.click()}
      onKeyDown={(e) => e.key === "Enter" && !disabled && input.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        if (!disabled && e.dataTransfer.files.length) onFiles(Array.from(e.dataTransfer.files));
      }}
      className={`flex cursor-pointer flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed p-6 text-center transition-colors ${
        over ? "border-teal-500 bg-teal-500/5" : c.border
      } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
    >
      <i className={`ri-upload-cloud-2-line text-3xl ${over ? "text-teal-500" : c.muted}`} />
      <p className={`text-sm font-medium ${c.text}`}>{label}</p>
      {hint && <p className={`text-xs ${c.muted}`}>{hint}</p>}
      <input
        ref={input}
        type="file"
        hidden
        accept={accept}
        multiple={multiple}
        data-testid="file-input"
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          e.target.value = "";
          if (files.length) onFiles(files);
        }}
      />
    </div>
  );
}

export function Spinner({ label }: { label?: string }) {
  const { t } = useTranslation("common");
  return (
    <div className="flex items-center gap-2 text-sm text-teal-600">
      <i className="ri-loader-4-line animate-spin text-lg" />
      {label ?? t("expenses.loading")}
    </div>
  );
}
